import { Paperclip } from "lucide-react";
import {
  gql,
  InternalAttachmentRunRecordOrderByColumn,
  Operation,
} from "types";
import { formatFileSize } from "utils/helpers";
import { FieldType, InternalResourceConfig } from "../lib/internal-resources";

const GET_ATTACHMENT_RUN_RECORD_LIST = gql(/* GraphQL */ `
  query InternalAttachmentRunRecordList(
    $pagination: Pagination
    $filters: FiltersExpression
    $orderBy: InternalAttachmentRunRecordOrderByInput
  ) {
    internalAttachmentRunRecordList(
      pagination: $pagination
      filters: $filters
      orderBy: $orderBy
    ) {
      count
      list {
        id
        runId
        nodeId
        itemIndex
        entityType
        entityId
        fileId
        fileUrl
        fileFilename
        fileSize
        createdAt
      }
    }
  }
`);

const renderItemIndex = (value: unknown) => {
  if (value === null || value === undefined) return "-";
  return String(value);
};

const renderFileFilename = (value: unknown) => {
  if (value === null || value === undefined) return "-";
  return String(value);
};

const renderFileSize = (value: unknown) => {
  if (value === null || value === undefined) return "-";
  const size = Number(value);
  if (Number.isNaN(size)) return "-";
  return formatFileSize(size);
};

export const attachmentRunRecordResourceConfig: InternalResourceConfig = {
  resourceName: "attachment-run-record",
  displayNameKey: "apps.admin.internalResources.attachmentRunRecord.title",
  basePath: "/internal/data/attachment-run-records",

  listQuery: GET_ATTACHMENT_RUN_RECORD_LIST,
  listQueryName: "internalAttachmentRunRecordList",

  fields: [
    {
      key: "runId",
      labelKey:
        "apps.admin.internalResources.attachmentRunRecord.columns.runId",
      type: FieldType.Uuid,
      sortKey: InternalAttachmentRunRecordOrderByColumn.RunId,
      width: 190,
    },
    {
      key: "nodeId",
      labelKey:
        "apps.admin.internalResources.attachmentRunRecord.columns.nodeId",
      type: FieldType.String,
      width: 140,
    },
    {
      key: "itemIndex",
      labelKey:
        "apps.admin.internalResources.attachmentRunRecord.columns.itemIndex",
      type: FieldType.String,
      format: renderItemIndex,
      width: 100,
    },
    {
      key: "entityType",
      labelKey:
        "apps.admin.internalResources.attachmentRunRecord.columns.entityType",
      type: FieldType.String,
      width: 160,
    },
    {
      key: "entityId",
      labelKey:
        "apps.admin.internalResources.attachmentRunRecord.columns.entityId",
      type: FieldType.Uuid,
      sortKey: InternalAttachmentRunRecordOrderByColumn.EntityId,
      width: 190,
    },
    {
      key: "fileId",
      labelKey:
        "apps.admin.internalResources.attachmentRunRecord.columns.fileId",
      type: FieldType.Uuid,
      width: 190,
    },
    {
      key: "fileFilename",
      labelKey:
        "apps.admin.internalResources.attachmentRunRecord.columns.fileFilename",
      type: FieldType.String,
      format: renderFileFilename,
      width: 220,
    },
    {
      key: "fileSize",
      labelKey:
        "apps.admin.internalResources.attachmentRunRecord.columns.fileSize",
      type: FieldType.String,
      format: renderFileSize,
      width: 120,
    },
    {
      key: "createdAt",
      labelKey:
        "apps.admin.internalResources.attachmentRunRecord.columns.createdAt",
      type: FieldType.DateTime,
      sortKey: InternalAttachmentRunRecordOrderByColumn.CreatedAt,
      width: 180,
    },
  ],

  list: {
    defaultSort: {
      column: InternalAttachmentRunRecordOrderByColumn.CreatedAt,
      direction: "desc",
    },
    searchFields: [
      {
        field: "attachment_run_record.run_id::text",
        op: Operation.Ilike,
      },
      {
        field: "attachment_run_record.entity_id",
        op: Operation.Ilike,
      },
    ],
  },

  navigation: {
    icon: <Paperclip size={16} />,
    order: 9,
  },
};
